import { Fragment, useMemo, useState } from "react";
import { ChevronRight, Workflow } from "lucide-react";
import { DsIcon, DsSpinner } from "../../../design-system";
import { SCREEN_ACCESS_ACTION_ORDER, groupScreensForAccess } from "../../../rbac/catalog";
import { ACTION_SHORT_LABELS, ACTION_HINTS, actionState, moduleStats } from "./roleAccessUtils";

const STATE_TEXT = { full: "Allowed", partial: "Some screens", none: "Not allowed", na: "Not applicable" };

/** Read-only switch-style indicator: on, off, or partial. */
function StateCell({ state }) {
  if (state === "na") {
    return (
      <span className="text-xs text-[hsl(var(--ds-border))]" aria-label={STATE_TEXT.na}>
        —
      </span>
    );
  }
  const track =
    state === "full"
      ? "bg-[hsl(var(--ds-primary))]"
      : state === "partial"
        ? "bg-[hsl(var(--ds-primary)/0.4)]"
        : "bg-[hsl(var(--ds-muted))] ring-1 ring-inset ring-[hsl(var(--ds-border))]";
  const knob = state === "full" ? "translate-x-3" : state === "partial" ? "translate-x-1.5" : "translate-x-0";
  return (
    <span
      role="img"
      aria-label={STATE_TEXT[state]}
      title={STATE_TEXT[state]}
      className={`relative inline-flex h-4 w-7 items-center rounded-full p-0.5 ${track}`}
    >
      <span className={`h-3 w-3 rounded-full bg-white shadow-sm ${knob}`} />
    </span>
  );
}

export default function RolePermissionMatrix({ role, catalog, access, usesAccess, loading }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [onlyGranted, setOnlyGranted] = useState(true);

  const rows = useMemo(
    () => catalog.map((mod) => ({ mod, stats: moduleStats(mod, access?.[mod.key]) })),
    [catalog, access]
  );
  const grantedCount = rows.filter((r) => r.stats.granted > 0).length;
  const shown = onlyGranted && grantedCount > 0 ? rows.filter((r) => r.stats.granted > 0) : rows;

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const shell = (body) => (
    <section
      aria-label="Permission matrix"
      className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]"
    >
      {body}
    </section>
  );

  if (!role) {
    return shell(
      <div className="flex flex-1 items-center justify-center p-8">
        <p className="ds-body text-[hsl(var(--ds-muted-fg))]">Select a role to see its permissions.</p>
      </div>
    );
  }

  if (!usesAccess) {
    return shell(
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))]">
          <DsIcon icon={Workflow} size="md" />
        </span>
        <h4 className="ds-h4 text-[hsl(var(--ds-fg))]">Workflow assignee only</h4>
        <p className="ds-body max-w-md text-[hsl(var(--ds-muted-fg))]">
          Action Based roles have no screen or data permissions. Pick this role as an assignee in
          Helpdesk, policy checklists and approval workflows. Change the role type to Access Based to
          grant screens.
        </p>
      </div>
    );
  }

  return shell(
    <>
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[hsl(var(--ds-border))] px-4 py-3">
        <div className="min-w-0">
          <h3 className="ds-h4 truncate text-[hsl(var(--ds-fg))]">Permissions · {role.name}</h3>
          <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
            {grantedCount} of {catalog.length} modules enabled. Expand a module to see each screen. Read-only;
            use Edit role to change.
          </p>
        </div>
        {grantedCount > 0 ? (
          <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs text-[hsl(var(--ds-fg))]">
            <input
              type="checkbox"
              checked={onlyGranted}
              onChange={(e) => setOnlyGranted(e.target.checked)}
              className="h-3.5 w-3.5 accent-[hsl(var(--ds-primary))]"
            />
            Enabled modules only
          </label>
        ) : null}
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center p-8">
          <DsSpinner />
        </div>
      ) : grantedCount === 0 && onlyGranted ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
          <h4 className="ds-h4 text-[hsl(var(--ds-fg))]">No permissions yet</h4>
          <p className="ds-body max-w-sm text-[hsl(var(--ds-muted-fg))]">
            Users with this role can&apos;t open any screen from it. Use Edit role to choose modules and
            permissions.
          </p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
          <table className="w-full min-w-[640px] border-collapse text-left">
            <thead className="sticky top-0 z-[1] bg-[hsl(var(--ds-surface))]">
              <tr className="border-b border-[hsl(var(--ds-border))]">
                <th scope="col" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                  Module / screen
                </th>
                {SCREEN_ACCESS_ACTION_ORDER.map((a) => (
                  <th
                    key={a}
                    scope="col"
                    title={ACTION_HINTS[a]}
                    className="w-[72px] px-1 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]"
                  >
                    {ACTION_SHORT_LABELS[a]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map(({ mod, stats }) => {
                const open = expanded.has(mod.key);
                const moduleAcc = access?.[mod.key];
                return (
                  <Fragment key={mod.key}>
                    <tr className="border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)]">
                      <th scope="row" className="px-2 py-2 font-normal">
                        <button
                          type="button"
                          onClick={() => toggle(mod.key)}
                          aria-expanded={open}
                          className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-[hsl(var(--ds-muted))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
                        >
                          <DsIcon
                            icon={ChevronRight}
                            size="sm"
                            className={`text-[hsl(var(--ds-muted-fg))] transition-transform duration-150 motion-reduce:transition-none ${open ? "rotate-90" : ""}`}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-[hsl(var(--ds-fg))]">
                              {mod.label}
                            </span>
                            <span className="block text-[11px] text-[hsl(var(--ds-muted-fg))]">
                              {stats.screensOn} of {stats.screenTotal} screens · {stats.granted}/{stats.total} permissions
                            </span>
                          </span>
                        </button>
                      </th>
                      {SCREEN_ACCESS_ACTION_ORDER.map((a) => (
                        <td key={a} className="px-1 py-2 text-center">
                          <StateCell state={actionState(mod.screens, moduleAcc, a)} />
                        </td>
                      ))}
                    </tr>
                    {open
                      ? groupScreensForAccess(mod.screens).map((group, gi) => (
                          <Fragment key={`${mod.key}-${group.label || gi}`}>
                            {group.label ? (
                              <tr className="border-b border-[hsl(var(--ds-border))]">
                                <td
                                  colSpan={SCREEN_ACCESS_ACTION_ORDER.length + 1}
                                  className="px-11 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]"
                                >
                                  {group.label}
                                </td>
                              </tr>
                            ) : null}
                            {group.screens.map((screen) => (
                              <tr key={screen.key} className="border-b border-[hsl(var(--ds-border))] last:border-0">
                                <th
                                  scope="row"
                                  className="py-1.5 pl-11 pr-2 text-sm font-normal text-[hsl(var(--ds-fg))]"
                                  title={screen.description || screen.label}
                                >
                                  {screen.label}
                                </th>
                                {SCREEN_ACCESS_ACTION_ORDER.map((a) => (
                                  <td key={a} className="px-1 py-1.5 text-center">
                                    <StateCell state={actionState([screen], moduleAcc, a)} />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </Fragment>
                        ))
                      : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
